import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, SkipForward, SkipBack, ChevronDown, X } from 'lucide-react';
import ReactPlayer from 'react-player';

const MobileMusicPlayer = ({
    currentTrack,
    isPlaying,
    togglePlay,
    nextTrack,
    volume,
    setVolume,
    isMuted,
    onReady,
    onEnded,
    playlists = {},
    currentPlaylistId,
    switchPlaylist,
    isCompact = false
}) => {
    const playerRef = useRef(null);
    const [showChannels, setShowChannels] = useState(false);
    const [progress, setProgress] = useState("0:00");
    const [needleRotation, setNeedleRotation] = useState(-45);

    // Simulate VU Meter movement
    useEffect(() => {
        if (!isPlaying) {
            setNeedleRotation(-45);
            return;
        }
        const interval = setInterval(() => {
            // Random movement between -20 and +20 degrees
            setNeedleRotation(Math.random() * 40 - 20);
        }, 100);
        return () => clearInterval(interval);
    }, [isPlaying]);

    // Skeuomorphic Styles
    const buttonBase = "transition-all duration-200 active:scale-95 flex items-center justify-center";
    const tactileButton = `${buttonBase} bg-[#f4f4f4] shadow-[3px_3px_6px_#d1d1d1,-3px_-3px_6px_#ffffff] active:shadow-[inset_2px_2px_5px_#d1d1d1,inset_-2px_-2px_5px_#ffffff]`;
    const concaveScreen = "bg-[#e8e8e8] shadow-[inset_2px_2px_5px_#bebebe,inset_-2px_-2px_5px_#ffffff]";

    // Speaker Grill Pattern
    const grillPattern = {
        backgroundImage: 'radial-gradient(#333 1px, transparent 1px)',
        backgroundSize: '4px 4px'
    };

    // Force play handler
    const handleTogglePlay = () => {
        // If we are about to play (currently paused), try to force start
        if (!isPlaying && playerRef.current) {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer) {
                if (internalPlayer.play) {
                    internalPlayer.play();
                } else if (internalPlayer.playVideo) {
                    // Fallback for YouTube if used here
                    internalPlayer.playVideo();
                }
            }
        }
        togglePlay();
    };

    // Render
    return (
        <>
            {/* Channel List Overlay (Shared) */}
            <div
                className={`absolute bottom-0 left-0 right-0 bg-[#f4f4f4] rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] z-50 transition-transform duration-300 ease-out border-t border-white/50 ${showChannels ? 'translate-y-0' : 'translate-y-full'
                    }`}
                style={{ height: '70vh' }}
            >
                <div className="p-6 flex items-center justify-between">
                    <span className="font-bold text-xs uppercase tracking-widest text-[#888]">Select Channel</span>
                    <button
                        onClick={() => setShowChannels(false)}
                        className={`${tactileButton} w-8 h-8 rounded-full text-[#666]`}
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto h-full pb-20 space-y-3">
                    {Object.entries(playlists).map(([id, playlist]) => (
                        <button
                            key={id}
                            onClick={() => {
                                switchPlaylist(id);
                                setShowChannels(false);
                            }}
                            className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${currentPlaylistId === id
                                ? 'bg-[#e8e8e8] shadow-[inset_2px_2px_5px_#bebebe,inset_-2px_-2px_5px_#ffffff]'
                                : 'bg-[#f4f4f4] shadow-[3px_3px_6px_#d1d1d1,-3px_-3px_6px_#ffffff] active:shadow-[inset_2px_2px_5px_#d1d1d1,inset_-2px_-2px_5px_#ffffff]'
                                }`}
                        >
                            <span className={`font-bold text-sm uppercase tracking-wider ${currentPlaylistId === id ? 'text-[#333]' : 'text-[#666]'}`}>
                                {playlist.title}
                            </span>
                            {currentPlaylistId === id && <div className="w-2 h-2 rounded-full bg-[#AF2E1B] shadow-[0_0_5px_#AF2E1B]"></div>}
                        </button>
                    ))}
                </div>
            </div>

            {/* UI Layer: Compact vs Full */}
            {isCompact ? (
                /* Compact Player UI */
                <div className="bg-[#f4f4f4] px-4 py-3 flex items-center justify-between relative z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] border-t border-white/40 transition-all duration-300">
                    {/* Track Info */}
                    <button
                        onClick={() => setShowChannels(true)}
                        className="flex-1 flex items-center gap-3 min-w-0"
                    >
                        <div className="w-1 h-1 rounded-full bg-[#2e7d32] shadow-[0_0_4px_#2e7d32] flex-shrink-0"></div>
                        <div className="min-w-0 flex-1 text-left">
                            <div className="text-xs font-bold text-[#333] truncate leading-tight">
                                {currentTrack?.title || "Loading..."}
                            </div>
                            <div className="text-[9px] text-[#666] font-medium uppercase tracking-wide truncate">
                                {currentTrack?.artist || "Paradise FM"}
                            </div>
                        </div>
                    </button>

                    {/* Compact Controls */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={handleTogglePlay}
                            className={`${tactileButton} w-10 h-10 rounded-full text-[#333]`}
                        >
                            {isPlaying ? (
                                <Pause size={16} fill="currentColor" />
                            ) : (
                                <Play size={16} fill="currentColor" className="ml-0.5" />
                            )}
                        </button>
                        <button
                            onClick={nextTrack}
                            className={`${tactileButton} w-8 h-8 rounded-lg text-[#555]`}
                        >
                            <SkipForward size={14} fill="currentColor" />
                        </button>
                    </div>
                </div>
            ) : (
                /* Full Player UI - Detailed Braun Style */
                <div className="bg-[#f4f4f4] p-5 pb-8 flex flex-col gap-5 relative z-40 shadow-[0_-10px_30px_rgba(0,0,0,0.05)] border-t border-white/40">

                    {/* Top Row: Tuner & VU Meter */}
                    <div className="flex items-start justify-between gap-4">
                        {/* Tuner Section */}
                        <div className="flex-1">
                            <div className="text-[9px] font-bold text-[#888] uppercase tracking-widest mb-1 ml-1">TUNING</div>
                            <button
                                onClick={() => setShowChannels(true)}
                                className={`${concaveScreen} w-full h-12 rounded-lg flex items-center justify-between px-3 group active:bg-[#e0e0e0] transition-colors`}
                            >
                                <div className="flex items-center space-x-2 overflow-hidden">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#2e7d32] shadow-[0_0_4px_#2e7d32] flex-shrink-0"></div>
                                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#555] truncate">
                                        {playlists[currentPlaylistId]?.title || "CHANNEL"}
                                    </span>
                                </div>
                                <ChevronDown size={14} className="text-[#888] flex-shrink-0" />
                            </button>
                        </div>

                        {/* VU Meter Section */}
                        <div className="w-24">
                            <div className="text-[9px] font-bold text-[#888] uppercase tracking-widest mb-1 ml-1">SIGNAL</div>
                            <div className={`${concaveScreen} h-12 rounded-lg relative overflow-hidden flex items-center justify-center`}>
                                {/* Meter Scale */}
                                <div className="absolute inset-x-2 top-2 h-4 border-b border-[#aaa] flex justify-between px-1">
                                    <div className="w-px h-1 bg-[#aaa]"></div>
                                    <div className="w-px h-1 bg-[#aaa]"></div>
                                    <div className="w-px h-1 bg-[#aaa]"></div>
                                    <div className="w-px h-1 bg-[#AF2E1B]"></div>
                                </div>
                                {/* Needle */}
                                <div
                                    className="w-0.5 h-8 bg-[#333] origin-bottom absolute bottom-[-5px] transition-transform duration-100 ease-linear shadow-[1px_1px_2px_rgba(0,0,0,0.2)]"
                                    style={{ transform: `rotate(${needleRotation}deg)` }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    {/* Middle Row: Track Info & Volume Knob */}
                    <div className="flex gap-4 items-stretch">
                        {/* Track Info Screen */}
                        <div className={`${concaveScreen} flex-1 p-3 rounded-xl flex flex-col justify-between relative overflow-hidden min-h-[80px]`}>
                            <div className="z-10">
                                <div className="text-sm font-bold text-[#333] leading-tight line-clamp-2">
                                    {currentTrack?.title || "Loading..."}
                                </div>
                                <div className="text-[10px] text-[#666] font-medium uppercase tracking-wide mt-1 truncate">
                                    {currentTrack?.artist || "Paradise FM"}
                                </div>
                            </div>
                            <div className="font-mono text-[10px] font-bold text-[#444] self-end mt-2 bg-[#dcdcdc] px-1 rounded shadow-inner">
                                {progress}
                            </div>
                        </div>

                        {/* Volume Knob */}
                        <div className="flex flex-col items-center justify-end w-16">
                            <div className="text-[9px] font-bold text-[#888] uppercase tracking-widest mb-1">VOL</div>
                            <div className="relative w-14 h-14 rounded-full bg-[#f0f0f0] shadow-[3px_3px_6px_#d1d1d1,-3px_-3px_6px_#ffffff] flex items-center justify-center">
                                {/* Knob Indicator */}
                                <div
                                    className="w-1.5 h-6 bg-[#ccc] rounded-full absolute top-1 origin-bottom transition-transform duration-200"
                                    style={{ transform: `rotate(${(volume * 270) - 135}deg) translateY(-8px)` }}
                                >
                                    <div className="w-1.5 h-3 bg-[#AF2E1B] rounded-full"></div>
                                </div>
                                {/* Invisible Range Input for Interaction */}
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={(e) => setVolume && setVolume(parseFloat(e.target.value))}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row: Controls & Speaker Grill */}
                    <div className="flex items-center justify-between gap-4">
                        {/* Controls */}
                        <div className="flex items-center gap-3">
                            <button className={`${tactileButton} w-10 h-10 rounded-lg text-[#555]`}>
                                <SkipBack size={18} fill="currentColor" />
                            </button>

                            <button
                                onClick={handleTogglePlay}
                                className={`${tactileButton} w-16 h-16 rounded-full text-[#333] relative group`}
                            >
                                <div className="absolute inset-1.5 rounded-full bg-[#f4f4f4] shadow-[inset_2px_2px_5px_#d1d1d1,inset_-2px_-2px_5px_#ffffff] flex items-center justify-center">
                                    {isPlaying ? (
                                        <Pause size={24} fill="currentColor" className="text-[#333]" />
                                    ) : (
                                        <Play size={24} fill="currentColor" className="ml-1 text-[#333]" />
                                    )}
                                </div>
                            </button>

                            <button
                                onClick={nextTrack}
                                className={`${tactileButton} w-10 h-10 rounded-lg text-[#555]`}>
                                <SkipForward size={18} fill="currentColor" />
                            </button>
                        </div>

                        {/* Speaker Grill (Fills space) */}
                        <div className="flex-1 h-16 rounded-lg border border-black/5 shadow-inner relative overflow-hidden bg-[#e0e0e0]">
                            <div className="absolute inset-0 opacity-30" style={grillPattern}></div>
                            <div className="absolute bottom-1 right-2 font-mono text-[8px] font-bold text-[#888] tracking-widest">
                                STEREO
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Shared Player Component (Prevents unmounting between views) */}
            {/* NOTE: We use opacity-0 and fixed positioning instead of display:none (hidden) */}
            {/* because mobile browsers often block media playback from display:none elements. */}
            {/* We also use 1px dimensions because some widgets (SoundCloud) won't play if 0x0. */}
            <div className="fixed top-0 left-0 w-px h-px opacity-0 pointer-events-none -z-50 overflow-hidden">
                <ReactPlayer
                    ref={playerRef}
                    key={currentTrack?.src}
                    url={currentTrack?.src || ''}
                    playing={isPlaying && !!currentTrack?.src}
                    playsinline={true}
                    volume={isMuted ? 0 : volume}
                    onReady={onReady}
                    onEnded={onEnded}
                    onProgress={({ playedSeconds }) => {
                        const mins = Math.floor(playedSeconds / 60);
                        const secs = Math.floor(playedSeconds % 60);
                        setProgress(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
                    }}
                    width="100%"
                    height="100%"
                    config={{
                        soundcloud: {
                            options: {
                                auto_play: false,
                                visual: false
                            }
                        }
                    }}
                />
            </div>
        </>
    );
};

export default MobileMusicPlayer;
